import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Button, Col, message, Row } from "antd";
import { capitalize } from 'lodash';
import { ThemeConfig } from "theme";
import { useUser } from "helper/UserContext";
import { LoadingSpinnerView } from "components/LoadingSpinner/LoadingSpinner";
import { getCustomerPortalSession } from "api/helper";
import Api from "api";
import moment from "moment";
import config from 'config';

const LoginWrapper = styled.div`
  margin-top: 70px;
  h4 {
    color: #494848;
    text-align: center;
    text-transform: uppercase;
    font-size: 23px;
    line-height: 30px;
    padding: 0 0 10px;
    letter-spacing: 0.5px;
    margin: 0;
    font-family: inherit;
    font-weight: 500;
  }
`;

const FormWrapper = styled.div`
  width: 100%;
  color: ${ThemeConfig.colors.text};
  border-radius: 20px;
  background-color: ${ThemeConfig.colors.darkBlue};
  text-align: center;
  padding: 20px 0 20px 0;
  h3,
  h2 {
    color: ${ThemeConfig.colors.text};
  }
  .trial-badge {
    background: #faad14;
    color: #fff;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 500;
    display: inline-block;
    margin-bottom: 12px;
  }
`;

interface Props {
  checkoutInfo?: any;
}

const BillingForm = (props: Props) => {
  const [portalSession, setPortalSession] = useState<any>(null);
  const { user, setUser } = useUser();
  const [loading, setLoading] = useState<boolean>(true);

  const handleProfileData = (pData) => {
    setUser({ ...pData });
  };

  const getProfile = async () => {
    try {
      const { data } = await Api.get("/account/profile", {});
      handleProfileData({
        id: data.data.id,
        ...data.data.attributes,
      });
    } catch (e: any) {
      const errorMsg =
        (e?.response?.data?.messages?.[0]) || "Failed to Fetch Profile";
      console.log(errorMsg);
      message.error({ content: errorMsg, duration: 3 });
    }
    setLoading(false);
  };

  const initialize = async () => {
    // Only fetch portal session for legacy Stripe subscribers
    if (user?.subscription_expire_at || user?.subscription_auto_renew_at) {
      const _portalSession = await getCustomerPortalSession();
      _portalSession && _portalSession.data && setPortalSession(_portalSession.data);
    }
  };

  useEffect(() => {
    getProfile();
    initialize();
  }, []);

  if (loading || !user) return <LoadingSpinnerView size={80} />;

  const isTrial = user.csp_entitlements?.is_trialing;
  const trialEndsAt = user.csp_entitlements?.trial_ends_at;
  const hasLegacyStripe = !!(user.subscription_expire_at || user.subscription_auto_renew_at);

  return (
    <LoginWrapper>
      <Row>
        <Col lg={{ span: 12, offset: 6 }} span={24}>
          <FormWrapper>
            <h2>
              Your current plan is{" "}
              <strong>
                {capitalize((user.status || "").split("_").join(" "))}
              </strong>
            </h2>

            {isTrial && (
              <div>
                <span className="trial-badge">
                  Trial{trialEndsAt ? `: ${moment(trialEndsAt).diff(moment(), 'days')} days left` : ''}
                </span>
              </div>
            )}

            {hasLegacyStripe && (
              <>
                {user.subscription_expire_at && (
                  <h3>
                    Expires at:{" "}
                    {moment(user.subscription_expire_at).format("Do MMM YYYY")}
                  </h3>
                )}
                {user.subscription_auto_renew_at && (
                  <h3>
                    AutoRenews at:{" "}
                    {moment(user.subscription_auto_renew_at).format("Do MMM YYYY")}
                  </h3>
                )}
                {portalSession && (
                  <div style={{ marginBottom: 16 }}>
                    <a href={portalSession?.session_id?.url}>
                      Manage legacy Stripe subscription
                    </a>
                  </div>
                )}
              </>
            )}

            <div style={{ marginTop: 20 }}>
              <Button
                type="primary"
                size="large"
                href={config.csp_pricing_url}
                target="_blank"
                style={{ borderRadius: 8 }}
              >
                Manage Your Subscription
              </Button>
              <p style={{ marginTop: 12, fontSize: 13, opacity: 0.8 }}>
                Subscriptions are managed through the California Species Project
              </p>
            </div>
          </FormWrapper>
        </Col>
      </Row>
    </LoginWrapper>
  );
};

export default BillingForm;
